import {startCase} from "lodash"
import React, {useEffect, useMemo} from "react"
import {useToasts} from "react-toast-notifications"
import {useQueryState} from "use-location-state"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {useEsc} from "../../hooks/useEsc"
import {usePaging} from "../../hooks/usePaging"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {CheckPaymentsStatus, findCheckPayments} from "../../resources/checkPayments"
import {Cents} from "../../resources/transaction"
import {useAccessToken, useUserType} from "../../services/auth"
import {downloadFile} from "../../utilities/file"
import {DataTableActionHeader, DataTableCard, TablePending} from "../DataTable/DataTable"
import DatePickerWithPresets, {DatePickerPresetKeys} from "../DatePicker/DatePicker"
import {CurrencyRangeFilter} from "../Filter/CurrencyRangeFilter"
import {Filter} from "../Filter/Filter"
import SearchBox from "../SearchBox/SearchBox"
import {CheckPaymentsTable} from "./CheckPaymentsTable"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {findOrgs} from "../../resources/org"
import {Org} from "../../resources/org"
import {AsyncResult} from "../../types/asyncResult"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {ErrorDocument} from "../../resources/common"
import {CheckStopPayment, findStopPayments, StopPaymentStatus, StopPaymentType} from "../../resources/stopPayments"

export enum CheckPaymentsColumns {
	id = "Id",
	accountId = "Account",
	checkNumber = "Check Number",
	isAccountClosureCheck = "Purpose",
	org = "Org",
	customer = "Customer",
	amount = "Amount",
	status = "Status",
	additionalVerification = "Additional Verification",
	createdAt = "Created At",
	sendAt = "Send At",
	actions = "Actions",
}

export type AllowedCheckPaymentsColumns =
	| CheckPaymentsColumns.id
	| CheckPaymentsColumns.checkNumber
	| CheckPaymentsColumns.isAccountClosureCheck
	| CheckPaymentsColumns.org
	| CheckPaymentsColumns.accountId
	| CheckPaymentsColumns.customer
	| CheckPaymentsColumns.amount
	| CheckPaymentsColumns.status
	| CheckPaymentsColumns.additionalVerification
	| CheckPaymentsColumns.createdAt
	| CheckPaymentsColumns.sendAt
	| CheckPaymentsColumns.actions

interface CheckPaymentsProps {
	accountId?: string
	token?: string
	limit?: number
	includedColumns: AllowedCheckPaymentsColumns[]
	enableTitle?: boolean
	fullHeight?: boolean
	enableOrgFilter?: boolean
	enableStatusFilter?: boolean
	enableAmountFilter?: boolean
	enableAdditionalVerificationFilter?: boolean
	disableDrawer?: boolean
	enablePaging?: boolean
	enableExport?: boolean
	enableDateFilter?: boolean
	enableSearchBox?: boolean
	enablePurposeFilter?: boolean
	enableActions?: boolean
	title?: string
}

export enum PurposeOptions {
	GeneralUse = "General Use",
	AccountClosure = "Account Closure",
}

export enum AdditionalVerificationOptions {
	Required = "Required",
	NotRequired = "NotRequired",
	Approved = "Approved",
}

export default function CheckPayments({
	limit = 25,
	token,
	includedColumns,
	fullHeight,
	enableTitle,
	accountId,
	enableOrgFilter = false,
	enableStatusFilter = false,
	enableAmountFilter = false,
	enableAdditionalVerificationFilter = false,
	enablePaging = true,
	enableDateFilter = true,
	disableDrawer = false,
	enableExport = true,
	enableSearchBox = false,
	enablePurposeFilter = false,
	enableActions = false,
	title = "Check Payments",
}: CheckPaymentsProps) {
	const prefix = "checkPayments"
	const accessToken = token ?? useAccessToken()
	const [refreshToken, refresh] = useRefreshToken()
	const userType = useUserType()
	const {addToast} = useToasts()
	const [since, setSince] = useQueryState(`${prefix}-filter[since]`, "")
	const [until, setUntil] = useQueryState(`${prefix}-filter[until]`, "")
	const [filteredOrgs, setFilteredOrgs] = useQueryState<string[]>(`${prefix}-filter[orgs]`, [])
	const [query, setQuery] = useQueryState(`${prefix}-filter[query]`, "")
	const statusesOptions = new Map<CheckPaymentsStatus, string>(
		[
			CheckPaymentsStatus.PendingReview,
			CheckPaymentsStatus.MarkedForReturn,
			CheckPaymentsStatus.Processed,
			CheckPaymentsStatus.Returned,
			CheckPaymentsStatus.New,
			CheckPaymentsStatus.Pending,
			CheckPaymentsStatus.InDelivery,
			CheckPaymentsStatus.ReturnedToSender,
			CheckPaymentsStatus.Canceled,
			CheckPaymentsStatus.Delivered,
			CheckPaymentsStatus.InProduction,
			CheckPaymentsStatus.PendingCancellation,
			CheckPaymentsStatus.Rejected,
		].map((v) => [v, startCase(v)])
	)
	const [statuses, setStatuses] = useQueryState<Array<CheckPaymentsStatus>>(`${prefix}-filter[status]`, [])
	const purposeOptions = new Map(
		[PurposeOptions.AccountClosure, PurposeOptions.GeneralUse].map((v) => [v, startCase(v)])
	)
	const additionalVerificationOptions = new Map(
		[
			AdditionalVerificationOptions.Required,
			AdditionalVerificationOptions.NotRequired,
			AdditionalVerificationOptions.Approved,
		].map((v) => [v, startCase(v)])
	)
	const [purpose, setPurpose] = useQueryState<string[]>(`${prefix}-filter[purpose]`, [])
	const [additionalVerification, setAdditionalVerification] = useQueryState<string[]>(
		`${prefix}-filter[additionalVerification]`,
		[]
	)
	const [fromAmount, setFromAmount] = useQueryState<Cents | "">(`${prefix}-filter[fromAmount]`, "")
	const [toAmount, setToAmount] = useQueryState<Cents | "">(`${prefix}-filter[toAmount]`, "")
	const [sortBy, setSortBy] = useQueryState(`${prefix}-sort`, "-createdAt")
	const [searchBox, setIsSearchLoading] = SearchBox(query, "Search by Check Payment Id", 500, onSearch)
	const [selectedCheckPaymentId, setSelectedCheckPaymentId] = useQueryState("checkPaymentId", "")

	useEsc(() => setSelectedCheckPaymentId(""))

	function onSearch(searchTerm: string) {
		setQuery(searchTerm)
		reset(limit)
	}

	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) =>
			findCheckPayments(
				accessToken,
				offset,
				sortBy,
				query,
				"json",
				limit,
				statuses,
				filteredOrgs,
				since,
				until,
				accountId,
				fromAmount,
				toAmount,
				purpose,
				additionalVerification
			),
		(x) => x.data.length,
		[
			refreshToken,
			since,
			until,
			sortBy,
			[...statuses, ...filteredOrgs, ...purpose, ...additionalVerification].join(","),
			fromAmount,
			toAmount,
			query,
		],
		`${prefix}-`,
		true
	)

	useEffect(() => {
		result.match(
			() => null,
			() => setIsSearchLoading(false),
			() => setIsSearchLoading(false)
		)
	}, [result])

	const orgs =
		(userType === "unit" || userType === "bank") && enableOrgFilter
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()

	// Fetch active stop payments for the account if accountId is provided
	const activeStopPayments = accountId
		? useAsyncResult(
				() =>
					findStopPayments(
						accessToken,
						0,
						1000, // Get a large number to ensure we get all active stop payments
						"-createdAt",
						undefined, // customerId
						accountId,
						undefined, // tags
						undefined, // since
						undefined, // until
						undefined, // orgs
						undefined, // fromAmount
						undefined, // toAmount
						undefined, // noAmount
						undefined, // checkNumber
						[StopPaymentStatus.Active], // status - only active
						undefined, // banks
						undefined, // fromMinAmount
						undefined, // toMinAmount
						undefined, // noMinAmount
						undefined, // originatorName
						undefined, // noOriginatorName
						StopPaymentType.Check // type - only check stop payments
					),
				[accountId, refreshToken]
		  )
		: AsyncResult.pending<CheckStopPayment[], ErrorDocument>()

	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredOrgs, filteredOrgs, reset, limit),
		[orgs, setFilteredOrgs, filteredOrgs.join(","), reset, limit]
	)

	const purposeFilter = (
		<Filter
			statuses={purpose}
			setStatuses={setPurpose}
			onFilterFunc={() => reset(limit)}
			options={purposeOptions}
			title="Purpose"
		/>
	)

	const additionalVerificationFilter = (
		<Filter
			statuses={additionalVerification}
			setStatuses={setAdditionalVerification}
			onFilterFunc={() => reset(limit)}
			options={additionalVerificationOptions}
			title="Additional Verification"
		/>
	)

	const statusFilter = (
		<Filter
			statuses={statuses}
			setStatuses={setStatuses}
			onFilterFunc={() => reset(limit)}
			options={statusesOptions}
			title="Status"
		/>
	)

	const dateTime = (
		<DatePickerWithPresets
			calendarPosition={"right"}
			presets={[
				DatePickerPresetKeys.allTime,
				DatePickerPresetKeys.yesterday,
				DatePickerPresetKeys.lastMonth,
				DatePickerPresetKeys.last3Months,
				DatePickerPresetKeys.last6Months,
				DatePickerPresetKeys.last30Days,
				DatePickerPresetKeys.last7Days,
				DatePickerPresetKeys.custom,
			]}
			onDateChanged={(s, u) => {
				setSince(s)
				setUntil(u)
			}}
		/>
	)

	const amountFilter = (
		<CurrencyRangeFilter
			placeholder={"Amount"}
			presets={[]}
			from={fromAmount}
			setFrom={setFromAmount}
			to={toAmount}
			setTo={setToAmount}
		/>
	)

	function toggleSort() {
		setSortBy(sortBy == "-createdAt" ? "createdAt" : "-createdAt")
		reset(limit)
	}

	async function exportCheckPayments() {
		const response = await findCheckPayments(
			accessToken,
			0,
			sortBy,
			query,
			"csv",
			null,
			statuses,
			filteredOrgs,
			since,
			until,
			accountId,
			fromAmount,
			toAmount,
			additionalVerification
		)

		response.match(
			(d) => downloadFile("CheckPayments.csv", d, "text/csv"),
			() => {
				addToast("Something went wrong", {appearance: "warning"})
			}
		)
		return true
	}

	return (
		<DataTableCard className="payments-card">
			<DataTableActionHeader
				enableSticky={fullHeight}
				dateTimePicker={enableDateFilter ? dateTime : null}
				searchBox={enableSearchBox ? searchBox : null}
				filters={[
					...(enableOrgFilter ? [orgFilter] : []),
					...(enableAmountFilter ? [amountFilter] : []),
					...(enableStatusFilter ? [statusFilter] : []),
					...(enablePurposeFilter ? [purposeFilter] : []),
					...(enableAdditionalVerificationFilter ? [additionalVerificationFilter] : []),
				]}
				exportFunc={enableExport ? exportCheckPayments : undefined}
				title={enableTitle ? title : null}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: checkPayments}) => {
					// Extract active stop payments data - check if we have a value property
					let activeStopPaymentsData: CheckStopPayment[] = []
					const asyncResult = activeStopPayments as any

					if (asyncResult.value) {
						const result = asyncResult.value
						activeStopPaymentsData = result?.data || []
					}

					return (
						<CheckPaymentsTable
							checkPayments={checkPayments.data}
							meta={checkPayments.menta}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							refresh={refresh}
							isUsingPaging={enablePaging}
							include={checkPayments.included}
							fullHeight={fullHeight}
							includedColumns={includedColumns}
							sortFunction={toggleSort}
							sortBy={sortBy}
							selectedCheckPayment={selectedCheckPaymentId}
							setSelectedCheckPayment={setSelectedCheckPaymentId}
							disableDrawer={disableDrawer}
							enableActions={enableActions}
							activeStopPayments={activeStopPaymentsData}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
